import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  TablePagination,
  InputLabel,
  Tooltip,
  TableSortLabel,
  Menu
} from "@mui/material";
import { AiOutlineClose } from "react-icons/ai";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { PiFileCsv } from "react-icons/pi";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useQuery } from "@tanstack/react-query";
import { fetchCountryBreakdown } from "../../api/dashboardApi";
import dayjs from "dayjs";
import axios from "axios";
import LocationDetail from "../Location/LocationDetail";

const Row = ({ row, formatNumber, locationList = [] }) => {
  const [open, setOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState({ data: [], status: true });
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedLocId, setSelectedLocId] = useState("");

  const fetchLocationInvoices = async (locationId) => {
    setSelectedLocId(locationId);
    setInvoiceLoading(true);
    setInvoiceModalOpen(true);
    try {
      const token = localStorage.getItem("tokenInv");
      const url = `${process.env.REACT_APP_URL}/get-invoice-details-by-location/${locationId}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      });
      setInvoiceDetails(response.data);
    } catch (error) {
      console.error("Failed to fetch location invoices", error);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const formatCurrency = (val) => {
    if (val == null) return "0.00";
    return val.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Check if this country has any non-USD currency
  const hasLocalCurrency = (row.currencies || []).some(c => c !== "USD");
  // Get the primary local currency label (non-USD)
  const localCurrencyLabel = (row.currencies || []).find(c => c !== "USD") || null;

  let localSpendLocalAmount = 0;
  let localSpendUsdAmount = 0;
  let usdOnlySpendAmount = 0;

  let localNetSpendLocalAmount = 0;
  let localNetSpendUsdAmount = 0;
  let usdOnlyNetSpendAmount = 0;

  if (hasLocalCurrency && localCurrencyLabel) {
    (row.locations || []).forEach(loc => {
      const locCurrencyRaw = Array.isArray(loc.currencies) ? loc.currencies : [loc.currency || loc.currencies || "USD"];
      const locCurrency = locCurrencyRaw.find(c => c && c !== "USD") || locCurrencyRaw[0] || "USD";
      
      const sLocal = (loc.total_amount_usd != null ? loc.total_amount_usd : loc.spend_amount) || 0;
      const nsLocal = loc.net_spend_amount || 0;
      const sUsd = (loc.total_converted_usd != null ? loc.total_converted_usd : loc.spend_amount_usd) || 0;
      const nsUsd = (loc.net_spend_amount_usd != null ? loc.net_spend_amount_usd : loc.net_spend_amount) || 0;

      if (locCurrency === localCurrencyLabel) {
        localSpendLocalAmount += sLocal;
        localNetSpendLocalAmount += nsLocal;
        localSpendUsdAmount += sUsd;
        localNetSpendUsdAmount += nsUsd;
      } else if (locCurrency === "USD") {
        usdOnlySpendAmount += sUsd;
        usdOnlyNetSpendAmount += nsUsd;
      }
    });
  }

  const renderBreakdownTooltip = (type) => {
    if (!hasLocalCurrency || !localCurrencyLabel) return null;
    
    const localAmount = type === 'spend' ? localSpendLocalAmount : localNetSpendLocalAmount;
    const usdConverted = type === 'spend' ? localSpendUsdAmount : localNetSpendUsdAmount;
    const usdOnly = type === 'spend' ? usdOnlySpendAmount : usdOnlyNetSpendAmount;
    const totalUsd = type === 'spend' ? row.total_spend_amount_usd : row.total_net_spend_amount_usd;

    return (
      <Box sx={{ p: 0.5, fontSize: "12px", minWidth: 220 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, borderBottom: "1px solid rgba(255,255,255,0.2)", pb: 0.5 }}>
          Calculation Breakdown
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <span>{localCurrencyLabel} Amount:</span>
          <span>{localCurrencyLabel} {formatCurrency(localAmount)}</span>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <span>Converted to USD:</span>
          <span>USD {formatCurrency(usdConverted)}</span>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <span>USD Only Amount:</span>
          <span>+ USD {formatCurrency(usdOnly)}</span>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, pt: 0.5, borderTop: "1px solid rgba(255,255,255,0.2)", fontWeight: 600 }}>
          <span>Total USD:</span>
          <span>USD {formatCurrency(totalUsd)}</span>
        </Box>
      </Box>
    );
  };

  const handleExportLocations = () => {
    const locations = row.locations || [];
    if (!locations.length) return;
    
    const headers = ["Location ID", "Spend (Local)", "NET Spend (Local)", "Spend (USD)", "NET Spend (USD)", "Invoice Currencies"];
    const csvRows = [headers.join(",")];
    
    locations.forEach(loc => {
      const locCurrencyRaw = Array.isArray(loc.currencies) ? loc.currencies : [loc.currency || loc.currencies || "USD"];
      const locCurrency = locCurrencyRaw.find(c => c && c !== "USD") || locCurrencyRaw[0] || "USD";
      const locSpendLocal = loc.total_amount_usd != null ? loc.total_amount_usd : loc.spend_amount;
      const locNetSpendLocal = loc.net_spend_amount;
      const locSpendUsd = loc.total_converted_usd != null ? loc.total_converted_usd : loc.spend_amount_usd;
      const locNetSpendUsd = loc.net_spend_amount_usd != null ? loc.net_spend_amount_usd : loc.net_spend_amount;
      
      const csvRow = [
        `"${loc.location_id || "N/A"}"`,
        locSpendLocal || 0,
        locNetSpendLocal || 0,
        locSpendUsd || 0,
        locNetSpendUsd || 0,
        `"${locCurrency}"`
      ];
      csvRows.push(csvRow.join(","));
    });
    
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `locations_export_${row.country || 'country'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          "&:hover": { backgroundColor: "#fcf9ff" },
        }}
      >
        <TableCell sx={{ width: 50 }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ color: "#7C2EA0" }}
          >
            {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
          {row.country || "N/A"}
        </TableCell>
        <TableCell align="center" sx={{ fontWeight: 600 }}>
          {row.location_count || 0}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, color: "#7C2EA0" }}>
          <Box>
            {hasLocalCurrency && localCurrencyLabel ? (
              <Tooltip title={renderBreakdownTooltip('spend')} arrow placement="top">
                <Box sx={{ cursor: "help", borderBottom: "1px dashed #7C2EA0", display: "inline-block" }}>
                  {formatCurrency(row.total_spend_amount_usd)}
                </Box>
              </Tooltip>
            ) : (
              <Box>{formatCurrency(row.total_spend_amount_usd)}</Box>
            )}
            {hasLocalCurrency && localSpendLocalAmount > 0 && (
              <Box sx={{ fontSize: "11px", color: "#8b8b8b", mt: 0.3 }}>
                {localCurrencyLabel} {formatCurrency(localSpendLocalAmount)}
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, color: "#2563EB" }}>
          <Box>
            {hasLocalCurrency && localCurrencyLabel ? (
              <Tooltip title={renderBreakdownTooltip('netSpend')} arrow placement="top">
                <Box sx={{ cursor: "help", borderBottom: "1px dashed #2563EB", display: "inline-block" }}>
                  {formatCurrency(row.total_net_spend_amount_usd)}
                </Box>
              </Tooltip>
            ) : (
              <Box>{formatCurrency(row.total_net_spend_amount_usd)}</Box>
            )}
            {hasLocalCurrency && localNetSpendLocalAmount > 0 && (
              <Box sx={{ fontSize: "11px", color: "#8b8b8b", mt: 0.3 }}>
                {localCurrencyLabel} {formatCurrency(localNetSpendLocalAmount)}
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {(row.currencies || []).join(", ") || "-"}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                my: 2,
                mx: 1,
                backgroundColor: "#faf8fc",
                borderRadius: "10px",
                border: "1px solid #ede5f3",
                overflow: "hidden",
              }}
            >
              {/* Subtable Header */}
              <Box sx={{ px: 2, py: 1.2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #ede5f3", backgroundColor: "#f5f0fa" }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#7C2EA0" }}>
                  Locations ({(row.locations || []).length})
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {(row.currencies || []).map(cur => (
                      <Box key={cur} sx={{ fontSize: "10px", fontWeight: 600, px: 1, py: 0.2, borderRadius: "4px", backgroundColor: cur === "USD" ? "#e8f5e9" : "#fff3e0", color: cur === "USD" ? "#2e7d32" : "#e65100" }}>
                        {cur}
                      </Box>
                    ))}
                  </Box>
                  {(row.locations || []).length > 0 && (
                    <Tooltip title="Export Locations CSV" placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={handleExportLocations}
                        sx={{
                          padding: "2px",
                          backgroundColor: "#fff",
                          border: "1px solid #e2d5ed",
                          "&:hover": { backgroundColor: "#fcf9ff" }
                        }}
                      >
                        <PiFileCsv size={16} color="#7C2EA0" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              <TableContainer sx={{ maxHeight: 300, overflow: "auto" }}>
                <Table size="small" aria-label="locations">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: "11px", color: "#6b5b7b", textTransform: "uppercase", letterSpacing: "0.5px", backgroundColor: "#f5f0fa", borderBottom: "2px solid #ede5f3", py: 1 }}>
                        Location ID
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "11px", color: "#6b5b7b", textTransform: "uppercase", letterSpacing: "0.5px", backgroundColor: "#f5f0fa", borderBottom: "2px solid #ede5f3", py: 1 }}>
                        Spend (Local)
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "11px", color: "#6b5b7b", textTransform: "uppercase", letterSpacing: "0.5px", backgroundColor: "#f5f0fa", borderBottom: "2px solid #ede5f3", py: 1 }}>
                        NET Spend (Local)
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "11px", color: "#6b5b7b", textTransform: "uppercase", letterSpacing: "0.5px", backgroundColor: "#f5f0fa", borderBottom: "2px solid #ede5f3", py: 1 }}>
                        Spend (USD)
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "11px", color: "#6b5b7b", textTransform: "uppercase", letterSpacing: "0.5px", backgroundColor: "#f5f0fa", borderBottom: "2px solid #ede5f3", py: 1 }}>
                        NET Spend (USD)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(row.locations || []).map((loc, idx) => {
                      const locCurrencyRaw = Array.isArray(loc.currencies) ? loc.currencies : [loc.currency || loc.currencies || "USD"];
                      const locCurrency = locCurrencyRaw.find(c => c && c !== "USD") || locCurrencyRaw[0] || "USD";
                      const isLocNonUsd = locCurrency !== "USD";
                      const locSpendLocal = loc.total_amount_usd != null ? loc.total_amount_usd : loc.spend_amount;
                      const locNetSpendLocal = loc.net_spend_amount;
                      const locSpendUsd = loc.total_converted_usd != null ? loc.total_converted_usd : loc.spend_amount_usd;
                      const locNetSpendUsd = loc.net_spend_amount_usd != null ? loc.net_spend_amount_usd : loc.net_spend_amount;

                      return (
                      <TableRow
                        key={loc.location_id}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? "#fff" : "#faf8fc",
                          "&:hover": { backgroundColor: "#f3edf8" },
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <TableCell sx={{ fontSize: "12px", py: 1.2, borderBottom: "1px solid #f0eaf5" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {loc.location_name && loc.location_name !== "-" ? (
                              <Tooltip title={loc.location_name} arrow placement="top">
                                <span 
                                  onClick={() => fetchLocationInvoices(loc.location_id)}
                                  style={{ cursor: "pointer", borderBottom: "1px dashed #2563EB", fontWeight: 500, color: "#2563EB" }}
                                >
                                  {loc.location_id}
                                </span>
                              </Tooltip>
                            ) : (
                              <span 
                                onClick={() => fetchLocationInvoices(loc.location_id)}
                                style={{ cursor: "pointer", fontWeight: 500, color: "#2563EB", textDecoration: "underline" }}
                              >
                                {loc.location_id}
                              </span>
                            )}
                            <Box sx={{
                              fontSize: "9px", fontWeight: 700, px: 0.8, py: 0.15, borderRadius: "3px", lineHeight: "14px",
                              backgroundColor: isLocNonUsd ? "#fff3e0" : "#e8f5e9",
                              color: isLocNonUsd ? "#e65100" : "#2e7d32",
                            }}>
                              {locCurrency}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 500, py: 1.2, borderBottom: "1px solid #f0eaf5", color: "#334155" }}>
                          {isLocNonUsd ? `${locCurrency} ` : "USD "}{formatCurrency(locSpendLocal)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 500, py: 1.2, borderBottom: "1px solid #f0eaf5", color: "#334155" }}>
                          {isLocNonUsd ? `${locCurrency} ` : "USD "}{formatCurrency(locNetSpendLocal)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 500, py: 1.2, borderBottom: "1px solid #f0eaf5", color: "#7C2EA0" }}>
                          {formatCurrency(locSpendUsd)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 500, py: 1.2, borderBottom: "1px solid #f0eaf5", color: "#2563EB" }}>
                          {formatCurrency(locNetSpendUsd)}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {/* Currency-wise subtotal rows */}
                    {(() => {
                      const locations = row.locations || [];
                      const currencyTotals = {};
                      locations.forEach((loc) => {
                        const locCurrencyRaw = Array.isArray(loc.currencies) ? loc.currencies : [loc.currency || loc.currencies || "USD"];
                        const locCurrency = locCurrencyRaw.find(c => c && c !== "USD") || locCurrencyRaw[0] || "USD";
                        if (!currencyTotals[locCurrency]) {
                          currencyTotals[locCurrency] = { spendLocal: 0, netSpendLocal: 0, spendUsd: 0, netSpendUsd: 0, count: 0 };
                        }
                        const t = currencyTotals[locCurrency];
                        t.spendLocal += (loc.total_amount_usd != null ? loc.total_amount_usd : loc.spend_amount) || 0;
                        t.netSpendLocal += loc.net_spend_amount || 0;
                        t.spendUsd += (loc.total_converted_usd != null ? loc.total_converted_usd : loc.spend_amount_usd) || 0;
                        t.netSpendUsd += (loc.net_spend_amount_usd != null ? loc.net_spend_amount_usd : loc.net_spend_amount) || 0;
                        t.count += 1;
                      });
                      const currencyKeys = Object.keys(currencyTotals);
                      if (locations.length <= 1 && currencyKeys.length <= 1) return null;
                      return currencyKeys.map((cur, cIdx) => {
                        const t = currencyTotals[cur];
                        const isNonUsd = cur !== "USD";
                        return (
                          <TableRow key={`subtotal-${cur}`} sx={{ backgroundColor: "#f0e6f5", ...(cIdx === 0 ? { "& td": { borderTop: "2px solid #d4bde6" } } : {}) }}>
                            <TableCell sx={{ fontSize: "11px", fontWeight: 700, color: "#7C2EA0", py: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                <Box sx={{
                                  fontSize: "9px", fontWeight: 700, px: 0.8, py: 0.15, borderRadius: "3px",
                                  backgroundColor: isNonUsd ? "#fff3e0" : "#e8f5e9",
                                  color: isNonUsd ? "#e65100" : "#2e7d32",
                                }}>
                                  {cur}
                                </Box>
                                Subtotal · {t.count} location{t.count > 1 ? "s" : ""}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 700, color: "#334155", py: 1 }}>
                              {isNonUsd ? `${cur} ` : "USD "}{formatCurrency(t.spendLocal)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 700, color: "#334155", py: 1 }}>
                              {isNonUsd ? `${cur} ` : "USD "}{formatCurrency(t.netSpendLocal)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 700, color: "#7C2EA0", py: 1 }}>
                              {formatCurrency(t.spendUsd)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 700, color: "#2563EB", py: 1 }}>
                              {formatCurrency(t.netSpendUsd)}
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <Dialog open={invoiceModalOpen} onClose={() => setInvoiceModalOpen(false)} maxWidth="xl" fullWidth>
        <DialogContent sx={{ p: 0, minHeight: '300px', display: 'flex', justifyContent: 'center', alignItems: invoiceLoading ? 'center' : 'flex-start', backgroundColor: "#f9f9f9" }}>
          {invoiceLoading ? (
            <CircularProgress />
          ) : (
            <Box sx={{ p: 2, width: "100%", overflow: 'hidden' }}>
              <LocationDetail data={invoiceDetails} contractNumber={selectedLocId} onClose={() => setInvoiceModalOpen(false)} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

const CountryBreakdownModal = ({
  open,
  onClose,
  initialFilters,
  formatNumber,
  countryList = [],
  locationList = [],
  canViewLocation,
  title = "Country Breakdown"
}) => {
  const [rangeType, setRangeType] = useState("all");
  const [dateRange, setDateRange] = useState({
    start: dayjs("2020-01-01"),
    end: dayjs(),
  });
  const [selectedCountry, setSelectedCountry] = useState(
    initialFilters?.country || "All"
  );
  const [selectedLoc, setSelectedLoc] = useState(
    initialFilters?.location_id || "All"
  );
  const [filteredLocations, setFilteredLocations] = useState(["All"]);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('total_spend_amount_usd');
  const [csvMenuAnchor, setCsvMenuAnchor] = useState(null);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sync with initialFilters if they change while modal is open
  useEffect(() => {
    if (initialFilters?.country) {
      setSelectedCountry(initialFilters.country);
    }
    if (initialFilters?.location_id) {
      setSelectedLoc(initialFilters.location_id);
    }
  }, [initialFilters]);

  // Set up filtered locations based on selected country
  useEffect(() => {
    if (selectedCountry === "All") {
      setFilteredLocations(["All"]);
    } else {
      const locList = ["All"];
      locationList?.forEach((el) => {
        const cntry = el?.updated_country || el?.country || "N/A";
        if (cntry.toLowerCase() === selectedCountry.toLowerCase()) {
          locList.push(el.location_id);
        }
      });
      setFilteredLocations(locList);
    }
  }, [selectedCountry, locationList]);

  // Handle range type changes
  useEffect(() => {
    if (rangeType === "custom") return;

    const today = dayjs();
    let start, end;

    switch (rangeType) {
      case "all":
        start = dayjs("2024-01-01");
        end = today;
        break;
      case "lastMonth":
        start = today.subtract(1, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "last3Months":
        start = today.subtract(3, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "last6Months":
        start = today.subtract(6, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "last12Months":
        start = today.subtract(12, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      default:
        start = dayjs("2024-01-01");
        end = today;
    }
    setDateRange({ start, end });
  }, [rangeType]);

  const handleReset = () => {
    setRangeType("all");
    const today = dayjs();
    setDateRange({
      start: dayjs("2020-01-01"),
      end: today,
    });
    setSelectedCountry("All");
    setSelectedLoc("All");
    setPage(0);
  };

  const currentFilters = {
    start_date: dateRange.start.format("YYYY-MM-DD"),
    end_date: dateRange.end.format("YYYY-MM-DD"),
    vendor_id: initialFilters?.vendor_id,
    location_id: selectedLoc,
    country: selectedCountry,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["countryBreakdown", currentFilters],
    queryFn: () => fetchCountryBreakdown(currentFilters),
    enabled: open,
  });

  const countriesData = data?.countries || [];

  const sortedCountriesData = React.useMemo(() => {
    const sortedData = [...countriesData];
    sortedData.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'currencies') {
        aValue = (a.currencies || []).join(", ");
        bValue = (b.currencies || []).join(", ");
      } else if (orderBy === 'country') {
        aValue = (a.country || "").toLowerCase();
        bValue = (b.country || "").toLowerCase();
      }
      
      if (aValue < bValue) return order === 'asc' ? 1 : -1;
      if (aValue > bValue) return order === 'asc' ? -1 : 1;
      return 0;
    });
    return sortedData;
  }, [countriesData, order, orderBy]);

  const handleCsvClick = (event) => {
    if (countriesData.length > 0) {
      setCsvMenuAnchor(event.currentTarget);
    }
  };

  const handleCsvClose = () => {
    setCsvMenuAnchor(null);
  };

  const handleDownloadCSV = (isDetailed) => {
    handleCsvClose();
    if (!sortedCountriesData.length) return;

    // Define columns for CSV
    const headers = ["Country/Location", "Location Count", "Total Spend USD", "Total NET Spend USD", "Invoice Currencies"];
    const csvRows = [headers.join(",")];

    sortedCountriesData.forEach(row => {
      // Country row
      const countryValues = [
        `"${row.country || "N/A"}"`,
        row.location_count || 0,
        row.total_spend_amount_usd || 0,
        row.total_net_spend_amount_usd || 0,
        `"${(row.currencies || []).join(", ")}"`
      ];
      csvRows.push(countryValues.join(","));

      if (isDetailed) {
        // Location rows
        if (row.locations && row.locations.length > 0) {
          row.locations.forEach(loc => {
            const locCurrencies = Array.isArray(loc.currencies) ? loc.currencies.join(", ") : (loc.currency || loc.currencies || "-");
            const locSpendUsd = loc.total_converted_usd != null ? loc.total_converted_usd : loc.spend_amount_usd;
            const locNetSpendUsd = loc.net_spend_amount_usd != null ? loc.net_spend_amount_usd : loc.net_spend_amount;
            
            const locValues = [
              `"    - Location: ${loc.location_id || "-"}"`,
              `""`,
              locSpendUsd || 0,
              locNetSpendUsd || 0,
              `"${locCurrencies}"`
            ];
            
            csvRows.push(locValues.join(","));
          });
        }
      }
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Country_Breakdown_${dayjs().format("YYYYMMDD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const paginatedData = sortedCountriesData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {title}
          {data?.total_countries > 0 && (
            <Tooltip 
              title="Total Countries" 
              arrow 
              placement="top"
            >
              <Box
                component="span"
                sx={{
                  backgroundColor: "#F8EAFF",
                  color: "#7C2EA0",
                  fontSize: "0.85rem",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  fontWeight: 600,
                  border: "1px solid #7C2EA0",
                  ml: 1
                }}
              >
                {data.total_countries} Countries
              </Box>
            </Tooltip>
          )}
        </Box>
        <IconButton onClick={onClose}>
          <AiOutlineClose />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Filter Bar */}
        <Box
          sx={{
            mb: 2,
            pt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            justifyContent: "flex-end",
            flexWrap: "nowrap",
            overflowX: "auto",
            pb: 1,
            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#e0e0e0",
              borderRadius: "10px",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 100, flexShrink: 0 }}>
              <InputLabel id="cb-range-type-label" sx={{ fontSize: "13px" }}>Date</InputLabel>
              <Select
                labelId="cb-range-type-label"
                label="Date"
                value={rangeType}
                onChange={(e) => setRangeType(e.target.value)}
                sx={{
                  fontSize: "13px",
                  height: "32px",
                  "& .MuiSelect-select": { py: "4px" },
                }}
              >
                <MenuItem value="all" sx={{ fontSize: "13px" }}>All</MenuItem>
                <MenuItem value="lastMonth" sx={{ fontSize: "13px" }}>Last Month</MenuItem>
                <MenuItem value="last3Months" sx={{ fontSize: "13px" }}>Last 3 Months</MenuItem>
                <MenuItem value="last6Months" sx={{ fontSize: "13px" }}>Last 6 Months</MenuItem>
                <MenuItem value="last12Months" sx={{ fontSize: "13px" }}>Last 12 Months</MenuItem>
                <MenuItem value="custom" sx={{ fontSize: "13px" }}>Custom Range</MenuItem>
              </Select>
            </FormControl>

            {rangeType === "custom" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: 130,
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "13px",
                          },
                          "& .MuiInputBase-input": { py: "4px" },
                        },
                      },
                    }}
                    value={dateRange.start}
                    maxDate={dayjs()}
                    onChange={(v) => setDateRange({ ...dateRange, start: v })}
                  />
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: 130,
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "13px",
                          },
                          "& .MuiInputBase-input": { py: "4px" },
                        },
                      },
                    }}
                    value={dateRange.end}
                    minDate={dateRange.start}
                    maxDate={dayjs()}
                    onChange={(v) => setDateRange({ ...dateRange, end: v })}
                  />
                </Stack>
              </LocalizationProvider>
            )}

            {canViewLocation && (
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Autocomplete
                  size="small"
                  options={countryList}
                  value={selectedCountry}
                  sx={{
                    width: 120,
                    "& .MuiInputBase-root": {
                      height: "32px",
                      fontSize: "13px",
                      py: 0,
                    },
                    "& .MuiInputBase-input": { fontSize: "13px" },
                    "& .MuiInputLabel-root": { fontSize: "13px", top: "-2px" },
                  }}
                  onChange={(e, val) => {
                    setSelectedCountry(val || "All");
                    setSelectedLoc("All");
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Country" size="small" />
                  )}
                />
                <Autocomplete
                  size="small"
                  options={filteredLocations}
                  value={selectedLoc}
                  disabled={selectedCountry === "All"}
                  sx={{
                    width: 120,
                    "& .MuiInputBase-root": {
                      height: "32px",
                      fontSize: "13px",
                      py: 0,
                    },
                    "& .MuiInputBase-input": { fontSize: "13px" },
                    "& .MuiInputLabel-root": { fontSize: "13px", top: "-2px" },
                  }}
                  onChange={(e, val) => setSelectedLoc(val || "All")}
                  renderInput={(params) => (
                    <TextField {...params} label="Location" size="small" />
                  )}
                />
              </Stack>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {(rangeType !== "last6Months" ||
                selectedCountry !== "All" ||
                selectedLoc !== "All") && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleReset}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "13px",
                    paddingInline: "10px",
                    height: "32px",
                  }}
                >
                  Reset
                </Button>
              )}
              
              <Tooltip title="Download CSV" placement="top" arrow>
                <span
                  onClick={handleCsvClick}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#f5f5f5",
                    cursor: countriesData.length > 0 ? "pointer" : "default",
                    opacity: countriesData.length > 0 ? 1 : 0.5,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (countriesData.length > 0) {
                      e.currentTarget.style.backgroundColor = "#e0e0e0";
                    }
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  <PiFileCsv size={18} color="#333" />
                </span>
              </Tooltip>
              <Menu
                anchorEl={csvMenuAnchor}
                open={Boolean(csvMenuAnchor)}
                onClose={handleCsvClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    minWidth: 150
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => handleDownloadCSV(false)} sx={{ fontSize: '13px' }}>
                  Normal CSV
                </MenuItem>
                <MenuItem onClick={() => handleDownloadCSV(true)} sx={{ fontSize: '13px' }}>
                  Detailed CSV
                </MenuItem>
              </Menu>
            </Stack>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, overflowX: "hidden" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
              <CircularProgress sx={{ color: "#7C2EA0" }} />
            </Box>
          ) : isError ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">
                {error?.message || "Failed to load country breakdown data."}
              </Typography>
            </Box>
          ) : countriesData.length > 0 ? (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <TableContainer sx={{ maxHeight: "60vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", width: 50, zIndex: 11 }} />
                      <TableCell
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === 'country'}
                          direction={orderBy === 'country' ? order : 'asc'}
                          onClick={() => handleRequestSort('country')}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                        >
                          Country/Location
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === 'location_count'}
                          direction={orderBy === 'location_count' ? order : 'asc'}
                          onClick={() => handleRequestSort('location_count')}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                        >
                          Location Count
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === 'total_spend_amount_usd'}
                          direction={orderBy === 'total_spend_amount_usd' ? order : 'asc'}
                          onClick={() => handleRequestSort('total_spend_amount_usd')}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                        >
                          Total Spend (USD)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === 'total_net_spend_amount_usd'}
                          direction={orderBy === 'total_net_spend_amount_usd' ? order : 'asc'}
                          onClick={() => handleRequestSort('total_net_spend_amount_usd')}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                        >
                          Total NET Spend (USD)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === 'currencies'}
                          direction={orderBy === 'currencies' ? order : 'asc'}
                          onClick={() => handleRequestSort('currencies')}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                        >
                          Invoice Currencies
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((row, index) => (
                      <Row key={row.country || index} row={row} formatNumber={formatNumber} locationList={locationList} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={countriesData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          ) : (
            <Box sx={{ p: 8, textAlign: "center" }}>
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{ fontStyle: "italic" }}
              >
                No data available matching your search.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CountryBreakdownModal;
